import { useTranslation } from 'react-i18next';
import type { MouseEvent } from 'react';
import { DateTime as DT } from 'luxon';
import LeadEventAttach from './LeadEventAttach';
import LeadEventAttachCloud from './LeadEventAttachCloud';
import LeadStatus from '../../Leads/LeadRows/LeadStatus';
import LeadNewStatus from '../LeadStatus/LeadNewStatus';
import LeadEventInfo from './LeadEventInfo';
import vconst from 'utils/helpers/structs/vconst';
import conf from 'conf/conf';
 
interface LeadEventFormProps {
    newEvent: any;
    setNewEvent: React.Dispatch<React.SetStateAction<any>>;
    disabled?: boolean;
    progress: number | boolean;
    setProgress: (value: number | boolean) => void;
    cloudProgress: number | boolean;
    pendingCloudFiles: File[];
    setPendingCloudFiles: React.Dispatch<React.SetStateAction<File[]>>;
}

const millisToIsoDate = (millis: number | null | undefined): string => {
    const value = Number(millis || 0);
    if (!Number.isFinite(value) || value <= 0) return '';
    return DT.fromMillis(value, { zone: 'local' }).toISODate() || '';
};

const LeadEventForm = ({
    newEvent,
    setNewEvent,
    disabled = false,
    progress,
    setProgress,
    cloudProgress,
    pendingCloudFiles,
    setPendingCloudFiles,
}: LeadEventFormProps) => {
    const { t } = useTranslation(['Leads']);
    const isPostponed = Number(newEvent?.statusID) === vconst.leadPostponed;
    const todayIso = DT.now().setZone('local').toISODate() || '';

    const handleClickStatus = (_e: MouseEvent, statusID: number) => {
        setNewEvent((prev: any) => ({
            ...prev,
            statusID,
            leadPendingDt: statusID === vconst.leadPostponed ? prev?.leadPendingDt || 0 : 0,
        }));
    };

    const handlePendingDtChange = (isoDate: string) => {
        const parsed = DT.fromISO(String(isoDate || '').trim(), { zone: 'local' });
        const leadPendingDt = parsed.isValid ? parsed.endOf('day').toMillis() : 0;
        setNewEvent((prev: any) => ({
            ...prev,
            leadPendingDt,
        }));
    };
 
    return (
        <>
            <LeadEventInfo disabled={disabled} newEvent={newEvent} setNewEvent={setNewEvent} />

            {!conf.cloudflareMode 
                ?(<LeadEventAttach
                    disabled={disabled}
                    progress={progress}
                    setProgress={setProgress}
                    newEvent={newEvent}
                    setNewEvent={setNewEvent}/>)
                :(<LeadEventAttachCloud 
                    disabled={disabled}
                    progress={cloudProgress} 
                    files={pendingCloudFiles} 
                    setFiles={setPendingCloudFiles} />)}

            {newEvent?.statusID !== vconst.leadAccept && (
                <div className="mb-3">
                    <label className="form-label text-muted">{t('leadStatus.labelStatus')}</label>
                    <div className="form-control d-flex justify-content-between align-items-center">
                        <div className="ms-1">
                            <LeadStatus leadStatus={newEvent?.statusID || 0} />
                        </div>
                        <LeadNewStatus
                            disabled={disabled}
                            showAcceptStatus={false}
                            title={t('common:buttons.change')}
                            onClick={handleClickStatus}
                        />
                    </div>
                </div>
            )}

            {isPostponed && (
                <div className="mb-3">
                    <label className="form-label text-muted" htmlFor="lead-pending-dt">
                        {t('leadStatus.labelPendingDt')}
                    </label>
                    <input
                        id="lead-pending-dt"
                        className="form-control"
                        type="date"
                        required
                        disabled={disabled}
                        min={todayIso}
                        value={millisToIsoDate(newEvent?.leadPendingDt)}
                        onChange={(e) => handlePendingDtChange(e.target.value)}
                    />
                </div>
            )}
        </>
    );
};

export default LeadEventForm;
